import type { HTMLAttributes, ReactNode } from 'react';

import { ButtonSystem } from '@/components/Buttons';

/**
 * feed-genre-filter — the feed is not empty, the FILTER emptied it.
 *
 * Saying "no recommendations" here would turn a setting into a phantom bug:
 * the sounds are there, a chip is hiding them. So this state names the cause
 * and offers the way out.
 *
 * Extracted from `home.tsx` for two reasons. It was unreachable by any test
 * (buried in a 900-line screen component), and it lived inside `CenterState` —
 * a flex ROW (`items-center justify-center`, no `flex-col`). That container
 * serves three states; the other two have a single child, so nothing ever
 * revealed the row layout until a second child was added and landed BESIDE the
 * text, with `mt-3` doing nothing horizontally.
 */
export default function FilterHiddenAllState({
  title,
  cta,
  onClear,
  children,
  ...rest
}: {
  /** Already translated and already agreed in number by the caller. */
  title: string;
  cta: string;
  onClear: () => void;
  /** Pull-to-refresh spinner, kept as a slot so this stays presentational. */
  children?: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-testid="discover-hidden-all"
      className="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:px-6 tw:pb-[var(--tab-bar-inset,0px)]"
      // An empty screen has no deck to pull on, so the state itself must carry
      // the gesture — same contract as the existing empty state in home.tsx.
      {...rest}
    >
      {children}
      <p className="tw:max-w-[260px] tw:text-center tw:text-base tw:text-grey-2">{title}</p>
      <ButtonSystem
        variant="secondary"
        size="s"
        label={cta}
        onClick={onClear}
        data-testid="discover-hidden-all-clear"
      />
    </div>
  );
}
